import React from 'react';

const DashboardView = ({ patients = [], schedules = [], staff = [], currentUser, selectedService, centerType, onNavigate }) => {
    const safeCurrentUser = currentUser || { role: 'Administrateur', id: '0', name: 'Administrateur' };
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to the start of the day

    const getCenterLabel = () => {
        if (centerType === 'hospital') return 'Centre Hospitalier';
        if (centerType === 'health_center_bed') return 'Centre de Santé (avec lit)';
        if (centerType === 'health_center_no_bed') return 'Centre de Santé (sans lit)';
        if (centerType === 'health_center_72h') return 'Centre de Santé (observation 72h+)';
        return 'Centre de soins';
    };

    // Today's schedules
    const todaysSchedules = schedules.filter(s => {
        const scheduleDate = new Date(s.date);
        scheduleDate.setHours(0, 0, 0, 0);
        return scheduleDate.getTime() === today.getTime();
    });

    // Filter for current user if not an admin
    const myTodaysSchedules = safeCurrentUser.role === 'Administrateur'
        ? todaysSchedules
        : todaysSchedules.filter(s => s.staffId === safeCurrentUser.id.toString());

    // Patients with consultations today
    const patientsToday = patients.filter(p =>
        p.consultations && p.consultations.some(c => {
            const consultDate = new Date(c.date);
            consultDate.setHours(0, 0, 0, 0);
            return consultDate.getTime() === today.getTime();
        })
    );

    const consultationsTodayCount = patients.reduce((acc, p) => {
        const todayConsultations = (p.consultations || []).filter(c => {
            const consultDate = new Date(c.date);
            consultDate.setHours(0, 0, 0, 0);
            return consultDate.getTime() === today.getTime();
        });
        return acc + todayConsultations.length;
    }, 0);

    const styles = {
        container: { padding: '20px' },
        headerBox: { marginBottom: '30px', backgroundColor: '#eef4ff', padding: '24px 26px', borderRadius: '16px', border: '1px solid #d1e3ff' },
        headerTitle: { fontSize: '1.65rem', margin: '0 0 8px 0', color: '#102a43' },
        headerText: { margin: 0, color: '#334e68', lineHeight: 1.7 },
        highlight: { color: '#0d3b66', fontWeight: 700 },
        statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '30px' },
        statCard: { padding: '22px', backgroundColor: '#fff', borderRadius: '16px', boxShadow: '0 20px 28px rgba(15,23,42,0.08)', textAlign: 'center' },
        statValue: { fontSize: '2.3em', fontWeight: 'bold', margin: '0', color: '#102a43' },
        statLabel: { fontSize: '1em', color: '#627d98', marginTop: '10px' },
        section: { marginTop: '30px', backgroundColor: '#fff', padding: '24px', borderRadius: '16px', boxShadow: '0 20px 28px rgba(15,23,42,0.08)' },
        sectionHeader: { marginTop: '0', marginBottom: '16px', color: '#102a43', fontSize: '1.15rem' },
        sectionText: { color: '#334e68', lineHeight: 1.75 },
        cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '18px' },
        cardWidget: { padding: '18px', borderRadius: '14px', backgroundColor: '#f7fbff', border: '1px solid #d9e6f8', minHeight: '120px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' },
        cardTitle: { margin: '0 0 10px', color: '#0d3b66', fontWeight: 700 },
        cardText: { margin: 0, color: '#334e68', lineHeight: 1.6 },
        list: { listStyle: 'none', padding: '0', margin: 0 },
        listItem: { borderBottom: '1px solid #e7eef8', padding: '15px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
        button: { marginTop: '18px', padding: '12px 18px', borderRadius: '10px', backgroundColor: '#0d3b66', border: 'none', color: 'white', cursor: 'pointer' },
        buttonAlt: { marginTop: '18px', padding: '12px 18px', borderRadius: '10px', backgroundColor: 'transparent', border: '1px solid #0d3b66', color: '#0d3b66', cursor: 'pointer' }
    };

    const getServiceDetail = () => {
        if (!selectedService) return 'Aucun service choisi.';
        if (/Observation/i.test(selectedService)) {
            if (centerType === 'health_center_no_bed') {
                return 'Mode observation sans lit : 2 à 24h maximum. Après 24h, transfert recommandé.';
            }
            if (centerType === 'health_center_bed') {
                return 'Mode observation avec lit : jusqu\'à 72h ou plus. Après 72h, transfert recommandé.';
            }
            if (centerType === 'hospital') {
                return 'Observation hospitalière : prise en charge avec lit et monitoring.';
            }
        }
        if (/Hospitalisation/i.test(selectedService)) {
            return 'Hospitalisation active : suivi des patients en lit avec dossier complet.';
        }
        return `Service ${selectedService} sélectionné pour le suivi et la prise en charge.`;
    };

    return (
        <div style={styles.container}>
            <div style={styles.headerBox}>
                <h2 style={styles.headerTitle}>Tableau de Bord - Bonjour, {safeCurrentUser.name}</h2>
                <p style={styles.headerText}>
                    <span style={styles.highlight}>{getCenterLabel()}</span> • Service sélectionné : <span style={styles.highlight}>{selectedService || 'Aucun service choisi'}</span>
                </p>
                {!selectedService && onNavigate && (
                    <button style={styles.button} type="button" onClick={onNavigate}>Choisir un service</button>
                )}
                {selectedService && onNavigate && (
                    <button style={styles.buttonAlt} type="button" onClick={onNavigate}>Changer de service</button>
                )}
            </div>

            <div style={styles.statsGrid}>
                <div style={styles.statCard}>
                    <p style={styles.statValue}>{patients.length}</p>
                    <p style={styles.statLabel}>Patients au Total</p>
                </div>
                <div style={styles.statCard}>
                    <p style={styles.statValue}>{consultationsTodayCount}</p>
                    <p style={styles.statLabel}>Consultations Aujourd\'hui</p>
                </div>
                <div style={styles.statCard}>
                    <p style={styles.statValue}>{myTodaysSchedules.length}</p>
                    <p style={styles.statLabel}>Mes Gardes Aujourd\'hui</p>
                </div>
                <div style={styles.statCard}>
                    <p style={styles.statValue}>{staff.length}</p>
                    <p style={styles.statLabel}>Membres du Personnel</p>
                </div>
            </div>
            {selectedService && (
                <div style={styles.section}>
                    <h3 style={styles.sectionHeader}>Vous travaillez sur le service :</h3>
                    <p style={styles.sectionText}>Le tableau de bord est personnalisé pour le service <strong>{selectedService}</strong>. Toutes les actions suivantes concerneront ce service.</p>
                </div>
            )}
            <div style={styles.section}>
                <h3>Mon Planning du Jour</h3>
                {myTodaysSchedules.length > 0 ? (
                    <ul style={styles.list}>
                        {myTodaysSchedules.map(s => (
                            <li key={s.id} style={styles.listItem}>
                                <span><strong>{s.staffName}</strong> ({s.staffRole})</span>
                                <span>{s.shift}</span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p>Aucun service prévu pour vous aujourd'hui.</p>
                )}
            </div>
        </div>
    );
};

export default DashboardView;
